<template>
  <div class="container py-4">
    <div class="page-card mb-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">Stock transfers</h4>

        <icon-button
          :func="() => (showCreate = true)"
          :icon="'plus'"
          :title="'New transfer'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'New transfer'"
        />
      </div>

      <b-table
        :items="transfers"
        :fields="fields"
        :busy="loading"
        responsive
        hover
        small
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.value)">{{
            data.value
          }}</b-badge>
        </template>
        <template #cell(actions)="data">
          <icon-button
            :func="() => $router.push(`/transfers/${data.item.id}`)"
            :icon="'eye'"
            :title="'View transfer'"
            :classes="'akkurate-gray-btn btn-sm'"
            :btnName="''"
          />
          <icon-button
            v-if="data.item.status === 'Draft'"
            :func="() => shipTransfer(data.item.id)"
            :icon="'truck'"
            :title="'Ship transfer'"
            :classes="'akkurate-blue-btn btn-sm'"
            :btnName="''"
          />
          <icon-button
            v-if="data.item.status === 'InTransit'"
            :func="() => receiveTransfer(data.item.id)"
            :icon="'check'"
            :title="'Receive'"
            :classes="'akkurate-green-btn btn-sm'"
            :btnName="''"
          />
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="showCreate"
      title="New transfer"
      size="lg"
      @ok="createTransfer"
    >
      <b-form-group label="From store">
        <b-form-select
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          v-model="form.fromStoreId"
          :options="storeOptions"
        />
      </b-form-group>
      <b-form-group label="To store">
        <b-form-select
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          v-model="form.toStoreId"
          :options="storeOptions"
        />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="form.notes" rows="2" />
      </b-form-group>

      <h6 class="mt-3">Items</h6>
      <div
        v-for="(item, idx) in form.items"
        :key="idx"
        class="row g-2 mb-2 align-items-end"
      >
        <div class="col-7">
          <b-form-select
            class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
            v-model="item.productId"
            :options="productOptions"
          >
            <template #first>
              <b-form-select-option :value="null" disabled
                >Select product</b-form-select-option
              >
            </template>
          </b-form-select>
        </div>
        <div class="col-3">
          <b-form-input
            class="form-control"
            v-model.number="item.quantity"
            type="number"
            min="1"
            placeholder="Qty"
          />
        </div>
        <div class="col-2">
          <icon-button
            :func="() => form.items.splice(idx, 1)"
            :icon="'times'"
            :title="'Remove item'"
            :classes="'akkurate-danger-bg btn-sm'"
            :btnName="''"
          />
        </div>
      </div>
      <icon-button
        :func="() => form.items.push({ productId: null, quantity: 1 })"
        :icon="'plus'"
        :title="'Add line'"
        :classes="'akkurate-green-btn btn-size2'"
        :btnName="'Add line'"
      />

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Save'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Save'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";
import { icon } from "@fortawesome/fontawesome-svg-core";

export default {
  name: "Transfers",
  components: {
    IconButton,
  },
  data() {
    return {
      transfers: [],
      products: [],
      allStores: [],
      loading: false,
      showCreate: false,
      form: {
        fromStoreId: null,
        toStoreId: null,
        notes: "",
        items: [{ productId: null, quantity: 1 }],
      },
      fields: [
        { key: "transferNumber", label: "Transfer #" },
        { key: "fromStoreName", label: "From" },
        { key: "toStoreName", label: "To" },
        { key: "status", label: "Status" },
        { key: "createdByName", label: "Created by" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId", "userStores"]),
    storeOptions() {
      return this.allStores.map((s) => ({ value: s.id, text: s.name }));
    },
    productOptions() {
      return this.products.map((p) => ({
        value: p.id,
        text: `${p.sku} — ${p.name}`,
      }));
    },
  },
  mounted() {
    this.form.fromStoreId = this.selectedStoreId;
    this.load();
    this.loadStores();
    this.loadProducts();
  },
  methods: {
    statusVariant(status) {
      if (status === "Received") return "success";
      if (status === "InTransit") return "info";
      return "secondary";
    },
    async loadStores() {
      this.allStores = await ApiService.get("stores");
    },
    async loadProducts() {
      this.products = await ApiService.get("products", {
        storeId: this.selectedStoreId,
      });
    },
    async load() {
      this.loading = true;
      try {
        this.transfers = await ApiService.get("transfers", {
          storeId: this.selectedStoreId,
        });
      } finally {
        this.loading = false;
      }
    },
    async createTransfer(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        await ApiService.post("transfers", {
          fromStoreId: this.form.fromStoreId,
          toStoreId: this.form.toStoreId,
          notes: this.form.notes || null,
          items: this.form.items
            .filter((i) => i.productId && i.quantity > 0)
            .map((i) => ({ productId: i.productId, quantity: i.quantity })),
        });
        this.showCreate = false;
        this.form = {
          fromStoreId: this.selectedStoreId,
          toStoreId: null,
          notes: "",
          items: [{ productId: null, quantity: 1 }],
        };
        await this.load();
        toast.fire({
          title: "Transfer created",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Create failed",
          icon: "danger",
        });
      }
    },
    async shipTransfer(id) {
      try {
        await ApiService.patch(`transfers/${id}/ship`);
        await this.load();
        toast.fire({
          title: "Transfer shipped",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Ship failed",
          icon: "danger",
        });
      }
    },
    async receiveTransfer(id) {
      try {
        await ApiService.patch(`transfers/${id}/receive`);
        await this.load();
        toast.fire({
          title: "Transfer received",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Receive failed",
          icon: "danger",
        });
      }
    },
  },
};
</script>
